import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  HomeIcon as HomeOutline,
  DocumentTextIcon as DocumentTextOutline,
  WrenchScrewdriverIcon as WrenchScrewdriverOutline,
  InboxStackIcon as InboxStackOutline,
  CalendarDaysIcon as CalendarDaysOutline,
} from '@heroicons/react/24/outline'
import {
  HomeIcon as HomeSolid,
  DocumentTextIcon as DocumentTextSolid,
  WrenchScrewdriverIcon as WrenchScrewdriverSolid,
  InboxStackIcon as InboxStackSolid,
  CalendarDaysIcon as CalendarDaysSolid,
} from '@heroicons/react/24/solid'
import { useSession } from '@/hooks/useSession'

interface Tab {
  to: string
  label: string
  exact: boolean
  OutlineIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  SolidIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>
}

// EP-135 Tier 1a — the tab set is now DERIVED from the logged-in person's
// active contracts instead of hardcoded. Tenant-only customers see the
// original 4 tabs (Home, Invoices, Maintenance, Parcels). A pure shared
// owner sees the 4 tenant tabs PLUS Meetings (a building-level event, not
// lease-scoped). A dual-role person sees the same 5 tabs as a pure shared
// owner. The legacy hardcoded 4-tab array is gone — the doc's §3a
// Blocker 3.
const baseTabs: Tab[] = [
  { to: '/', label: 'nav.home', exact: true, OutlineIcon: HomeOutline, SolidIcon: HomeSolid },
  { to: '/invoices', label: 'nav.invoices', exact: false, OutlineIcon: DocumentTextOutline, SolidIcon: DocumentTextSolid },
  { to: '/maintenance', label: 'nav.maintenance', exact: false, OutlineIcon: WrenchScrewdriverOutline, SolidIcon: WrenchScrewdriverSolid },
  { to: '/parcels', label: 'nav.parcels', exact: false, OutlineIcon: InboxStackOutline, SolidIcon: InboxStackSolid },
]

const meetingsTab: Tab = {
  to: '/meetings',
  label: 'nav.meetings',
  exact: false,
  OutlineIcon: CalendarDaysOutline,
  SolidIcon: CalendarDaysSolid,
}

export default function BottomNav() {
  const { t } = useTranslation('common')
  const { data: customer } = useSession()

  // Governance role = holds at least one active OwnerContract. Tenant-only
  // customers stay on the 4-tab set (the legacy behaviour is preserved
  // until this person logs in with a different lease_code).
  const hasGovernanceRole =
    customer?.contracts.some((c) => c.type === 'OwnerContract') ?? false

  const tabs = hasGovernanceRole ? [...baseTabs, meetingsTab] : baseTabs

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-base-100 border-t border-base-300 z-40 pb-safe">
      <div className="max-w-[430px] mx-auto flex" style={{ height: '56px' }}>
        {tabs.map((tab) => {
          const { OutlineIcon, SolidIcon } = tab
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.exact}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors ${
                  isActive ? 'text-primary' : 'text-dark-100'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive
                    ? <SolidIcon className="w-6 h-6" />
                    : <OutlineIcon className="w-6 h-6" />
                  }
                  <span className="text-[11px] leading-none">{t(tab.label)}</span>
                </>
              )}
            </NavLink>
          )
        })}
      </div>
    </nav>
  )
}
